/**
 * StatCard Component
 * KPI card with icon, value, trend, and optional sparkline
 */
import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { DesktopTheme, getScoreColor } from '../theme/desktopTheme';

const StatCard = ({
  title,
  value,
  suffix = '',
  previousValue,
  icon,
  color,
  onPress,
  style,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Calculate trend
  const trend = previousValue !== undefined && previousValue !== null
    ? value - previousValue
    : null;

  const trendPercent = previousValue && previousValue > 0
    ? ((value - previousValue) / previousValue * 100).toFixed(0)
    : null;

  const getTrendIcon = () => {
    if (trend === null) return null;
    if (trend > 0) return '↑';
    if (trend < 0) return '↓';
    return '→';
  };

  const getTrendColor = () => {
    if (trend === null) return colors.textMuted;
    if (trend > 0) return colors.success;
    if (trend < 0) return colors.error;
    return colors.textMuted;
  };

  const cardColor = color || getScoreColor(value);

  return (
    <TouchableOpacity
      style={[
        styles.card,
        isHovered && styles.cardHovered,
        style,
      ]}
      onPress={onPress}
      onMouseEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
      onMouseLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      activeOpacity={0.9}
      disabled={!onPress}
    >
      {/* Header with icon and title */}
      <View style={styles.header}>
        {icon && (
          <View style={[styles.iconContainer, { backgroundColor: cardColor + '15' }]}>
            <Text style={[styles.icon, { color: cardColor }]}>{icon}</Text>
          </View>
        )}
        <Text style={styles.title} numberOfLines={1}>{title}</Text>
      </View>

      {/* Value */}
      <View style={styles.valueContainer}>
        <Text style={styles.value}>
          {typeof value === 'number' ? Math.round(value) : value}
          <Text style={styles.suffix}>{suffix}</Text>
        </Text>
      </View>

      {/* Trend */}
      {trend !== null && (
        <View style={styles.trendContainer}>
          <Text style={[styles.trendIcon, { color: getTrendColor() }]}>
            {getTrendIcon()}
          </Text>
          <Text style={[styles.trendValue, { color: getTrendColor() }]}>
            {Math.abs(trend).toFixed(0)}{suffix}
          </Text>
          {trendPercent && (
            <Text style={styles.trendPercent}>
              ({trendPercent > 0 ? '+' : ''}{trendPercent}%)
            </Text>
          )}
          <Text style={styles.trendLabel}> vs previous</Text>
        </View>
      )}

      {/* Color accent bar */}
      <View style={[styles.accentBar, { backgroundColor: cardColor }]} />
    </TouchableOpacity>
  );
};

const { colors, typography, spacing, borderRadius, shadows } = DesktopTheme;

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    position: 'relative',
    overflow: 'hidden',
    minWidth: 200,
    ...shadows.card,
    borderWidth: 1,
    borderColor: colors.border,
  },
  cardHovered: {
    ...shadows.md,
    borderColor: colors.primary,
    transform: [{ translateY: -2 }],
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  icon: {
    fontSize: 20,
  },
  title: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textSecondary,
    flex: 1,
  },

  // Value
  valueContainer: {
    marginBottom: spacing.sm,
  },
  value: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h1,
    color: colors.textPrimary,
  },
  suffix: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.h4,
    color: colors.textSecondary,
  },

  // Trend
  trendContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  trendIcon: {
    fontSize: typography.fontSize.body,
    marginRight: 2,
  },
  trendValue: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.small,
  },
  trendPercent: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
    marginLeft: 4,
  },
  trendLabel: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
  },

  // Accent
  accentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    borderTopLeftRadius: borderRadius.lg,
    borderTopRightRadius: borderRadius.lg,
  },
});

export default StatCard;
